/**
 * The jig shell: the document a jig's iframe loads from the jig origin.
 *
 * Jig pages need an origin of their own: Playground's module loader is a
 * service worker, which only runs in a cross-origin `allow-same-origin` frame.
 * Each jig gets its own, per project (see `jigOrigin`).
 *
 * HACKY: the shell is stateless: the launcher names the program and the
 * manifest's `fetch` origins in the URL, and nothing checks them, so a jig can
 * widen its own network by rewriting the URL or its manifest. The fix is
 * Hearth's model: the worker forwards jig shells to the api, which rebuilds each
 * one from the indexed manifest lowered through `grantedCapabilities`
 * (playground consent), behind a shell token the api mints for the launcher.
 */
import { compileAppPageSrcdoc } from "playground/apps/compile";
import { corralZone } from "playground/apps/corral";
import { WORKSPACE_ROUTE_PREFIX } from "playground/apps/loader/paths";
import { appLabelForId, parseAppHost } from "playground/apps/origin";
import { invariant } from "ts-invariant";

export const JIG_SHELL_PATH = "/jig-shell.html";

/** The jigs zone: its own registrable domain, apart from the studio's. */
const JIGS_ZONE = "bearjigs.com";

/**
 * The zone jig hosts live under for a request on `host`: `bearjigs.com`, as
 * corral serves it in dev. Plain localhost has no zone to map, so jigs use
 * `*.localhost`, which the browser resolves to the loopback itself.
 */
function jigZone(host: string): string {
  const hostname = host.split(":")[0] ?? "";
  return hostname === "localhost" || hostname.endsWith(".localhost")
    ? "localhost"
    : corralZone(JIGS_ZONE, host);
}

/**
 * The origin of one jig in one project, served alongside a studio at
 * `studio`: `<project>--<publisher>--<name>.<zone>`, so no two jigs, and no two
 * projects' copies of a jig, share origin-scoped storage.
 */
export function jigOrigin(
  studio: URL,
  projectId: string,
  jigId: string,
): string {
  invariant(projectId.startsWith("prj_"), `not a project id: ${projectId}`);
  const label = appLabelForId(projectId.slice("prj_".length), jigId);
  invariant(
    label,
    `jig ${jigId} can't be served: its name must be a lowercase slug short enough for one DNS label`,
  );
  const port = studio.port ? `:${studio.port}` : "";
  return `${studio.protocol}//${label}.${jigZone(studio.host)}${port}`;
}

/** Whether `host` is a jig origin: what the dev server answers. */
export function isJigHost(host: string): boolean {
  return parseAppHost(host, jigZone(host)) !== null;
}

export function jigShellUrl(
  origin: string,
  program: string,
  fetchAllowlist: readonly string[],
): string {
  const url = new URL(JIG_SHELL_PATH, origin);
  url.searchParams.set("program", program);
  for (const allowed of fetchAllowlist)
    url.searchParams.append("fetch", allowed);
  return url.href;
}

/** Build the shell for a {@link jigShellUrl} request. */
export function jigShellHtml(url: URL, pageRuntime: string): Promise<string> {
  const program = url.searchParams.get("program");
  invariant(
    program !== null && program.startsWith(WORKSPACE_ROUTE_PREFIX),
    `jig program must be a ${WORKSPACE_ROUTE_PREFIX} path`,
  );
  return compileAppPageSrcdoc({
    mode: "program",
    fetchAllowlist: url.searchParams.getAll("fetch"),
    runtimeScript: pageRuntime,
    programUrl: program,
    assetOrigin: url.origin,
  });
}
